import { useSelector } from 'react-redux'
import { Redirect, Route, Switch } from 'react-router'

import {
  ProtectedRoute,
  type IProtectedRouteProps,
} from '@webapp/app/components'
import { ApproverOverwatchView } from '@webapp/containers/ApproverOverwatchView'
import {
  makeSelectEntitlementAccessConfig,
  makeSelectFeatureSwitches,
} from '@webapp/core/stores/global'
import { useFeatureFlag } from '@webapp/hooks'
import { useAppRoutes } from '@webapp/hooks/useAppRoutes'
import { ClosingMonthsPage } from '@webapp/modules/accounts-payable/pages/ClosingMonthsPage'
import {
  ManagePaymentAccountsPage,
  ManagePaymentAccountsPageTabs,
} from '@webapp/modules/accounts-payable/pages/ManagePaymentAccountsPage'
import { SettingsExpenseFormPage } from '@webapp/modules/expense/pages/SettingsExpenseFormPage'
import { AccountCodeCorrectionPage } from '@webapp/modules/purchasing/pages/AccountCodeCorrectionPage'
import { CatalogItemAccessPage } from '@webapp/modules/purchasing/pages/CatalogItemAccessPage'
import { CustomizeOrderFormPage } from '@webapp/modules/purchasing/pages/CustomizeOrderFormPage'
import { CustomizePurchaseOrderFormPage } from '@webapp/modules/purchasing/pages/CustomizePurchaseOrderFormPage'
import { CustomizeUnitTypesPage } from '@webapp/modules/purchasing/pages/CustomizeUnitTypesPage'
import { AccountCodeCorrectionTabs } from '@webapp/modules/purchasing/pages/types'
import {
  PunchOutGroupPage,
  PunchOutSuppliersPage,
  SettingsBillFormPage,
  SettingsImportPage,
  SettingsManageBankAccountsPage,
  SettingsManageFinancialAccountPage,
  SettingsPage,
  SpendingCardCustomFieldsSettingsPage,
  SsoConfigurationPage,
  SupportAccessPage,
} from '@webapp/pages'
import {
  ApprovalRoutingPage,
  ApprovalRoutingPageTabTypes,
} from '@webapp/pages/ApprovalRoutingPage'
import { ContractSettingsPage } from '@webapp/pages/ContractSettingsPage'
import { MainCustomFieldDetailPage } from '@webapp/pages/MainCustomFieldDetailPage'
import {
  MainCustomFieldSetupPage,
  MainCustomFieldSetupPageTabs,
} from '@webapp/pages/MainCustomFieldSetupPage'
import { ProtectedSsoRoute } from '@webapp/pages/SsoConfigurationPage'
import {
  ManageChartOfAccountsPage,
  ManageCurrencyRatesPage,
  ManageTaxTypesPage,
} from '@webapp/platform/finance/pages'
import {
  Permissions,
  ManageDateFormatPage,
  ManageUsersPage,
  MFAPreferencesPage,
  ReportAccessPage,
} from '@webapp/platform/iam'
import { SettingsManageRolesPermissionsPage } from '@webapp/platform/iam/pages/SettingsManageRolesPermissionsPage'
import { UserDetailsPage } from '@webapp/platform/iam/pages/UserDetailsPage'
import {
  ExportDataPage,
  RequestedExportsPage,
} from '@webapp/platform/import-export'
import {
  LegalEntityDetailsPage,
  LegalEntityListPage,
  LegalEntityListPageTabTypes,
} from '@webapp/platform/legal-entities'
import {
  PrimaryCategoryConfigDetailsPage,
  PrimaryCategoryConfigListPage,
} from '@webapp/platform/organization-management'
import { PunchOutGroupConnectionLogsPage } from '@webapp/platform/punchout/pages/PunchoutGroupConnectionLogsPage'
import { BudgetCategoryManagementRouter } from '../BudgetCategoryManagementRouter'

const ProtectedSettingsRoute = ({
  path,
  component,
  isSuperuserRoleRequired,
  ...otherProps
}: Omit<IProtectedRouteProps, 'navigationPermissions'>) => {
  return (
    <ProtectedRoute
      path={path}
      component={component}
      navigationPermissions={['settings']}
      isSuperuserRoleRequired={isSuperuserRoleRequired}
      {...otherProps}
    />
  )
}

const mainCustomFieldTabFormat = Object.values(
  MainCustomFieldSetupPageTabs
).join('|')

export const SettingsRouter = () => {
  const { url, brp } = useAppRoutes()

  const {
    disable_vendor_approvals_access: isVendorApprovalDisabled,
    disable_vendor_creation: isVendorCreationDisabled,
  } = useSelector(makeSelectFeatureSwitches())

  const shouldRedirectVendorApprovalRouting =
    isVendorApprovalDisabled || isVendorCreationDisabled

  const INT_1976_MIGRATE_MANAGE_USERS_AND_PERMISSIONS = useFeatureFlag(
    'INT_1976_MIGRATE_MANAGE_USERS_AND_PERMISSIONS'
  )

  const PSERV_255_DATEFORMAT_MIGRATION = useFeatureFlag(
    'PSERV_255_DATEFORMAT_MIGRATION'
  )

  const PSERV_346_BASE_MIGRATION = useFeatureFlag('PSERV_346_BASE_MIGRATION')

  const ARCH_615_MIGRATION_CATALOG_ACCESS = useFeatureFlag(
    'ARCH_615_MIGRATION_CATALOG_ACCESS'
  )

  const entitlementAccessConfig = useSelector(
    makeSelectEntitlementAccessConfig()
  )
  const isPurchasingEnabled = entitlementAccessConfig.module.purchasing.enabled
  const isExpenseEnabled = entitlementAccessConfig.module.expense.enabled

  return (
    <Switch>
      {/* Catalog Item Access Page */}
      {/* ARCH-615: flag-off falls back to the still-present Angular page. */}
      {!ARCH_615_MIGRATION_CATALOG_ACCESS && (
        <Redirect
          path={brp('CatalogItemAccessPage', { tab: ':tab' })}
          to='/#/settings/permissions'
        />
      )}
      {!isPurchasingEnabled && (
        <Redirect
          from={brp('CatalogItemAccessPage', { tab: ':tab' })}
          to={url('SettingsPage')}
        />
      )}
      <Redirect
        exact
        from='/settings/permissions'
        to={url('CatalogItemAccessPage', { tab: 'users' })}
      />
      <ProtectedSettingsRoute
        path={brp('CatalogItemAccessPage', { tab: ':tab(users|departments)' })}
        component={CatalogItemAccessPage}
        permissions={[Permissions.SETTINGS_MANAGE_CATALOG_LOCK]}
        featureFlags={['ARCH_615_MIGRATION_CATALOG_ACCESS']}
      />
      <Redirect
        from={brp('CatalogItemAccessPage', { tab: ':tab' })}
        to={url('CatalogItemAccessPage', { tab: 'users' })}
      />

      {/* Customize Unit Types Page */}
      {!isPurchasingEnabled && (
        <Redirect
          from={brp('CustomizeUnitTypesPage')}
          to={url('SettingsPage')}
        />
      )}
      <ProtectedSettingsRoute
        path={brp('CustomizeUnitTypesPage')}
        component={CustomizeUnitTypesPage}
        permissions={[Permissions.SETTINGS_ADD_UNITS]}
        featureFlags={['ARCH_468_MIGRATION_SETTINGS_UNIT']}
      />

      {/* Customize Request for Order Form Page */}
      {!isPurchasingEnabled && (
        <Redirect
          from={brp('CustomizeOrderFormPage')}
          to={url('SettingsPage')}
        />
      )}
      <ProtectedSettingsRoute
        path={brp('CustomizeOrderFormPage')}
        component={CustomizeOrderFormPage}
        permissions={[Permissions.SETTINGS_CUSTOMIZE_RFO_FIELDS]}
      />

      {/* Customize Purchase Order Form Page */}
      {!isPurchasingEnabled && (
        <Redirect
          from={brp('CustomizePurchaseOrderFormPage', { tab: '' })}
          to={url('SettingsPage')}
        />
      )}
      {/* Custom field management moved to MainCustomFieldSetupPage
          (PLAT-4936), so the bare URL now lands on the disclaimer tab.
          `exact` is load-bearing: the non-exact `from` would prefix-match the
          tabbed URLs too, including the redirect target itself. */}
      <Redirect
        exact
        from={brp('CustomizePurchaseOrderFormPage', { tab: '' })}
        to={url('CustomizePurchaseOrderFormPage', { tab: 'disclaimer' })}
      />
      <ProtectedSettingsRoute
        path={brp('CustomizePurchaseOrderFormPage', { tab: ':tab?' })}
        component={CustomizePurchaseOrderFormPage}
        permissions={[Permissions.SETTINGS_CUSTOMIZE_PO_FIELDS]}
      />

      {/* Account Code Correction Page */}
      {/* PSERV_346: flag-off falls back to the still-present Angular pages,
          preserving the orders vs expenses tab. */}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          path={brp('AccountCodeCorrectionPage', {
            tab: ':tab(expenses|expenses-audit-log)',
          })}
          to='/#/settings/approved-requests/expenses'
        />
      )}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          path={brp('AccountCodeCorrectionPage', { tab: ':tab?' })}
          to='/#/settings/approved-requests/orders'
        />
      )}
      {/* Entitlement-gated tab redirects must come BEFORE the protected route:
          customFilter does not receive entitlement data. */}
      {!isPurchasingEnabled && (
        <Redirect
          from={brp('AccountCodeCorrectionPage', {
            tab: ':tab(orders|orders-audit-log)',
          })}
          to={url('AccountCodeCorrectionPage', {
            tab: AccountCodeCorrectionTabs.EXPENSES,
          })}
        />
      )}
      {!isExpenseEnabled && (
        <Redirect
          from={brp('AccountCodeCorrectionPage', {
            tab: ':tab(expenses|expenses-audit-log)',
          })}
          to={url('AccountCodeCorrectionPage', {
            tab: AccountCodeCorrectionTabs.ORDERS,
          })}
        />
      )}
      <ProtectedSettingsRoute
        path={brp('AccountCodeCorrectionPage', {
          tab: ':tab(orders|orders-audit-log|expenses|expenses-audit-log)',
        })}
        component={AccountCodeCorrectionPage}
        permissions={[Permissions.SETTINGS_MANAGE_BUDGET_CORRECTION]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      {/* Handles empty AND unknown tab values */}
      <Redirect
        from={brp('AccountCodeCorrectionPage', { tab: ':tab?' })}
        to={url('AccountCodeCorrectionPage', {
          tab: isPurchasingEnabled
            ? AccountCodeCorrectionTabs.ORDERS
            : AccountCodeCorrectionTabs.EXPENSES,
        })}
      />

      {/* Manage Location Pages */}
      <ProtectedSettingsRoute
        path={brp('PrimaryCategoryConfigDetailsPage', {
          id: ':id',
          tab: ':tab',
        })}
        component={PrimaryCategoryConfigDetailsPage}
        permissions={[Permissions.SETTINGS_MANAGE_LOCATIONS]}
      />
      <ProtectedSettingsRoute
        path={brp('PrimaryCategoryConfigListPage', { tab: ':tab' })}
        component={PrimaryCategoryConfigListPage}
        permissions={[Permissions.SETTINGS_MANAGE_LOCATIONS]}
      />
      <Redirect
        path={brp('PrimaryCategoryConfigListPage', { tab: '' })}
        to={url('PrimaryCategoryConfigListPage', { tab: 'active' })}
      />

      {/* Manage Entities (Multi-Entity) */}
      {/* Constrain :tab so unknown values (typos, junk) fall through to the
          redirect below instead of silently rendering the Inactive list with
          no tab highlighted in the chrome. */}
      <ProtectedSettingsRoute
        path={brp('LegalEntityListPage', { tab: ':tab(active|inactive)' })}
        component={LegalEntityListPage}
        isSuperuserRoleRequired={true}
        customFilter={({ featureSwitches, featureFlags }) =>
          featureFlags.PLAT_4870_MULTI_ENTITY &&
          featureSwitches.multi_entity_enabled
        }
      />
      {/* Entity detail page. Must sit after the regex-constrained :tab route
          (so 'active'/'inactive' resolve to the list) and before the bare
          '/settings/entities/' redirect (so it can't swallow a uuid path). */}
      <ProtectedSettingsRoute
        path={brp('LegalEntityDetailsPage', { uuid: ':uuid' })}
        component={LegalEntityDetailsPage}
        isSuperuserRoleRequired={true}
        customFilter={({ featureSwitches, featureFlags }) =>
          featureFlags.PLAT_4870_MULTI_ENTITY &&
          featureSwitches.multi_entity_enabled
        }
      />
      <Redirect
        path={brp('LegalEntityListPage', { tab: '' })}
        to={url('LegalEntityListPage', {
          tab: LegalEntityListPageTabTypes.ACTIVE,
        })}
      />

      {/* Manage Financial Account Page */}
      <ProtectedSettingsRoute
        path={brp('SettingsManageFinancialAccountPage')}
        component={SettingsManageFinancialAccountPage}
        customFilter={({ featureSwitches, permissions }) => {
          return (
            permissions.includes(
              Permissions.SETTINGS_MANAGE_FINANCIAL_ACCOUNT
            ) &&
            (featureSwitches.bill_pay ||
              featureSwitches.enable_airwallex_onboarding ||
              featureSwitches.enable_airwallex_issuing)
          )
        }}
      />

      {/* Manage Currency Rates Page */}
      {/* PSERV_346: flag-off falls back to the still-present Angular page. */}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect path='/settings/currency' to='/#/settings/currency' />
      )}
      {/* Constrain :tab so unknown values (typos, junk) fall through to the
          redirect below instead of rendering a tab-less page. */}
      <ProtectedSettingsRoute
        path='/settings/currency/:tab(active|inactive)'
        component={ManageCurrencyRatesPage}
        permissions={[Permissions.SETTINGS_MANAGE_CURRENCY]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      <Redirect from='/settings/currency' to='/settings/currency/active' />

      {/* Manage Chart of Accounts Page */}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          path='/settings/chartofaccounts'
          to='/#/settings/chartofaccounts'
        />
      )}
      <ProtectedSettingsRoute
        path='/settings/chartofaccounts'
        component={ManageChartOfAccountsPage}
        permissions={[Permissions.SETTINGS_MANAGE_CHART_OF_ACCOUNTS]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />

      {/* Manage Tax Types Page */}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect path='/settings/tax' to='/#/settings/tax' />
      )}
      <ProtectedSettingsRoute
        path='/settings/tax'
        component={ManageTaxTypesPage}
        permissions={[Permissions.SETTINGS_MANAGE_TAXES]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />

      {/* Closing Months Page */}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          path={brp('ClosingMonthsPage')}
          to='/#/settings/closing-month'
        />
      )}
      <ProtectedSettingsRoute
        path={brp('ClosingMonthsPage')}
        component={ClosingMonthsPage}
        permissions={[Permissions.SETTINGS_ADD_MONTH_CLOSE]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />

      {/* Manage Payment Accounts Page (bank & credit card accounts) */}
      {/* PSERV_346: flag-off falls back to the still-present Angular page. */}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('ManagePaymentAccountsPage', { status: ':status?' })}
          to='/#/settings/financials/:status?'
        />
      )}
      {/* Constrain :status so unknown values fall through to the redirect
          below instead of rendering a tab-less page. */}
      <ProtectedSettingsRoute
        path={brp('ManagePaymentAccountsPage', {
          status: ':status(accounts|creditcards)',
        })}
        component={ManagePaymentAccountsPage}
        permissions={[Permissions.SETTINGS_MANAGE_CREDITCARDS]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      {/* Handles empty AND unknown status values */}
      {PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('ManagePaymentAccountsPage', { status: ':status?' })}
          to={url('ManagePaymentAccountsPage', {
            status: ManagePaymentAccountsPageTabs.ACCOUNTS,
          })}
        />
      )}

      <ProtectedSettingsRoute
        path={brp('SettingsManageBankAccountsPage')}
        component={SettingsManageBankAccountsPage}
        permissions={[Permissions.SETTINGS_MANAGE_LINKED_BANK_ACCOUNTS]}
        featureSwitches={['bill_pay', 'enable_direct_debit']}
      />

      <ProtectedSettingsRoute
        path={brp('SpendingCardCustomFieldsSettingsPage')}
        component={SpendingCardCustomFieldsSettingsPage}
        permissions={[Permissions.SPENDING_CARD_MANAGE_CARD]}
        customFilter={({ featureSwitches, permissions }) => {
          return (
            permissions.includes(Permissions.SPENDING_CARD_MANAGE_CARD) &&
            featureSwitches.enable_airwallex_issuing
          )
        }}
      />

      {/* PLAT-5405: the page was renamed from "Main custom field setup" and
          moved off /settings/main-custom-field-setup — preserve deep links. */}
      <Redirect
        from='/settings/main-custom-field-setup/:tab'
        to='/settings/custom-fields-setup/:tab'
      />
      <Redirect
        exact
        from='/settings/main-custom-field-setup'
        to='/settings/custom-fields-setup'
      />
      {/* Detail route must precede the list route: without `exact`, the list
          route's `:tab` would prefix-match `/…/{tab}/{id}` and swallow it. */}
      <ProtectedSettingsRoute
        path={brp('MainCustomFieldDetailPage', {
          tab: `:tab(${mainCustomFieldTabFormat})`,
          id: ':id',
        })}
        component={MainCustomFieldDetailPage}
        customFilter={({ permissions }) =>
          isPurchasingEnabled &&
          permissions.includes(Permissions.SETTINGS_CUSTOMIZE_RFO_FIELDS)
        }
      />
      <ProtectedSettingsRoute
        path={brp('MainCustomFieldSetupPage', {
          tab: `:tab(${mainCustomFieldTabFormat})`,
        })}
        component={MainCustomFieldSetupPage}
        customFilter={({ permissions }) =>
          isPurchasingEnabled && // enableCustomizeRequestForOrderForm, same as the old page
          permissions.includes(Permissions.SETTINGS_CUSTOMIZE_RFO_FIELDS)
        }
      />
      <Redirect
        path={brp('MainCustomFieldSetupPage', { tab: '' })}
        to={url('MainCustomFieldSetupPage', {
          tab: MainCustomFieldSetupPageTabs.LINE_ITEMS,
        })}
      />

      <ProtectedSettingsRoute
        path={brp('BudgetCategoryManagementRouter')}
        component={BudgetCategoryManagementRouter}
      />

      <ProtectedSettingsRoute
        path={'/settings/budget-categories'}
        component={BudgetCategoryManagementRouter}
        featureSwitches={['enable_flexible_spend_tracking']}
      />

      <ProtectedSettingsRoute
        path='/settings/import'
        component={SettingsImportPage}
        permissions={[Permissions.SETTINGS_IMPORT_DATA]}
      />

      {/* Export Data as CSV Pages */}
      {/* PSERV_346: flag-off falls back to the still-present Angular pages.
          The :exportType routes must come BEFORE the landing route pair so the
          landing route never swallows /settings/export-data/requested/*. */}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          from={brp('RequestedExportsPage', { exportType: ':exportType' })}
          to='/#/settings/exportcsv/requested/:exportType'
        />
      )}
      <ProtectedSettingsRoute
        path={brp('RequestedExportsPage', { exportType: ':exportType' })}
        component={RequestedExportsPage}
        permissions={[Permissions.SETTINGS_EXPORT_DATA]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect from={brp('ExportDataPage')} to='/#/settings/exportcsv' />
      )}
      <ProtectedSettingsRoute
        path={brp('ExportDataPage')}
        component={ExportDataPage}
        permissions={[Permissions.SETTINGS_EXPORT_DATA]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />

      <ProtectedSsoRoute
        path='/settings/sso'
        component={SsoConfigurationPage}
        navigationPermissions={['settings']}
      />

      <ProtectedSettingsRoute
        path='/settings/approver-view-access'
        component={ApproverOverwatchView}
      />

      <ProtectedSettingsRoute
        path={brp('SettingsSupportAccessPage')}
        component={SupportAccessPage}
        permissions={[Permissions.SETTINGS_GRANT_SUPPORT_ACCESS_CONSENT]}
      />

      <ProtectedSettingsRoute
        path={brp('SettingsBillFormPage')}
        component={SettingsBillFormPage}
        permissions={[Permissions.SETTINGS_CUSTOMIZE_BILL_COSTS]}
      />

      <ProtectedSettingsRoute
        path={brp('SettingsExpenseFormPage', { tab: ':tab?' })}
        component={SettingsExpenseFormPage}
        permissions={[Permissions.SETTINGS_CUSTOMIZE_EXPENSE_FORM]}
      />

      <ProtectedSettingsRoute
        path={brp('ContractSettingsPage')}
        component={ContractSettingsPage}
        permissions={[Permissions.SETTINGS_MANAGE_CONTRACTS]}
      />

      <ProtectedSettingsRoute
        path={brp('PunchOutGroupConnectionLogsPage', {
          supplier: ':supplier',
          groupId: ':groupId',
        })}
        component={PunchOutGroupConnectionLogsPage}
        permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
        featureSwitches={['punchout']}
        isSuperuserRoleRequired={true}
      />

      <ProtectedSettingsRoute
        path={brp('PunchOutGroupPage', {
          supplier: ':supplier',
          group: ':group',
        })}
        component={PunchOutGroupPage}
        permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
        featureSwitches={['punchout']}
      />

      <ProtectedSettingsRoute
        path={brp('PunchOutSuppliersPage', { supplier: ':supplier?' })}
        component={PunchOutSuppliersPage}
        permissions={[Permissions.SETTINGS_MANAGE_INTEGRATION]}
      />

      {!PSERV_255_DATEFORMAT_MIGRATION && (
        <Redirect
          path={url('SettingsManageDateFormatPage')}
          to='/#/settings/manage-date-format'
        />
      )}

      <ProtectedSettingsRoute
        path={brp('SettingsManageDateFormatPage')}
        component={ManageDateFormatPage}
        permissions={[Permissions.SETTINGS_MANAGE_DATE_FORMAT]}
        featureFlags={['PSERV_255_DATEFORMAT_MIGRATION']}
      />

      {/* PSERV_346: flag-off falls back to the Angular report-access page
          (its state + controller are restored in settings.access.reports). */}
      {!PSERV_346_BASE_MIGRATION && (
        <Redirect
          path={brp('SettingsReportAccessPage')}
          to='/#/settings/report-access'
        />
      )}
      <ProtectedSettingsRoute
        path={brp('SettingsReportAccessPage')}
        component={ReportAccessPage}
        permissions={[Permissions.SETTINGS_REPORTS_ACCESS]}
        featureFlags={['PSERV_346_BASE_MIGRATION']}
      />

      <ProtectedRoute
        path={brp('SettingsMFAPreferencesPage')}
        component={MFAPreferencesPage}
        navigationPermissions={['settings']}
      />

      {shouldRedirectVendorApprovalRouting && (
        <Redirect
          path={brp('SettingsApprovalRoutingPage', {
            tab: ApprovalRoutingPageTabTypes.VENDORS,
          })}
          to={brp('SettingsApprovalRoutingPage', {
            tab: ApprovalRoutingPageTabTypes.REQUESTS,
          })}
        />
      )}

      <ProtectedSettingsRoute
        path={brp('SettingsApprovalRoutingPage', { tab: ':tab' })}
        component={ApprovalRoutingPage}
        permissions={[Permissions.SETTINGS_APPROVAL_ROUTING]}
      />

      <Redirect
        path={brp('SettingsApprovalRoutingPage', {
          tab: '',
        })}
        to={brp('SettingsApprovalRoutingPage', {
          tab: ApprovalRoutingPageTabTypes.REQUESTS,
        })}
      />

      <ProtectedSettingsRoute
        path={brp('SettingsManageUsersPage', { status: ':status' })}
        component={ManageUsersPage}
        permissions={[Permissions.SETTINGS_MANAGE_USERS]}
        featureFlags={['INT_1976_MIGRATE_MANAGE_USERS_AND_PERMISSIONS']}
      />

      <ProtectedSettingsRoute
        path={brp('UserDetailsPage', { userId: ':userId' })}
        component={UserDetailsPage}
        permissions={[Permissions.SETTINGS_MANAGE_USERS]}
        featureFlags={['INT_1976_MIGRATE_MANAGE_USERS_AND_PERMISSIONS']}
      />

      {!INT_1976_MIGRATE_MANAGE_USERS_AND_PERMISSIONS && (
        <Redirect
          path={url('SettingsManageRolesPermissionsPage')}
          to='/#/settings/rolemanagement'
        />
      )}

      <ProtectedSettingsRoute
        path={brp('SettingsManageRolesPermissionsPage')}
        component={SettingsManageRolesPermissionsPage}
        permissions={[Permissions.SETTINGS_MANAGE_ROLES]}
        featureFlags={['INT_1976_MIGRATE_MANAGE_USERS_AND_PERMISSIONS']}
      />

      <ProtectedSettingsRoute
        exact
        path={brp('SettingsPage')}
        component={SettingsPage}
      />

      <Route path='*' render={() => <Redirect to='/' />} />
    </Switch>
  )
}
